import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { Textarea } from '@/components/ui/Textarea';
import { 
  Users, 
  Mail, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Copy,
  Eye, 
  Power, 
  PowerOff,
  Save,
  RefreshCw,
  TestTube,
  Search,
  X,
  CheckCircle2,
  Smartphone,
  Kanban,
  UserPlus,
  Phone,
  FileText,
  PhoneCall,
  ClipboardList,
  CalendarClock,
} from 'lucide-react';
import { ZehMotocaAgendamentoTabs } from '@/components/zehmotoca/ZehMotocaAgendamentoTabs';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import {
  emptyGrupo,
  flattenGruposToItens,
  itensApiToGrupos,
  type ChecklistGrupoForm,
} from '@/constants/oficinaChecklistAcoes';
import { ChecklistTemplateGruposEditor } from '@/components/oficina/ChecklistTemplateGruposEditor';
import { ZehMotocaUsuariosTable } from '@/components/zehmotoca/ZehMotocaUsuariosTable';
import {
  GMAIL_SMTP_PRESET_465,
  GMAIL_SMTP_PRESET_587,
  isGmailSmtpHost,
  normalizeSmtpHostInput,
} from '@/constants/smtpGmail';

// Interfaces
interface Usuario {
  id: string;
  email: string;
  phone?: string;
  cpfcnpj?: string;
  clienteFornecedor?: {
    id: string;
    nome: string;
    codigo?: string;
    cpfCnpj?: string;
    email?: string;
    telefone?: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EmailTemplate {
  id: string;
  nome: string;
  tipo: 'first_access' | 'reset_password';
  assunto: string;
  corpoHtml: string;
  corpoTexto?: string;
  variaveis?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EmailConfig {
  id: string;
  provider: 'smtp' | 'sendgrid';
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPassword?: string;
  smtpSecure?: boolean;
  sendgridApiKey?: string;
  fromEmail: string;
  fromName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Indicacao {
  id: string;
  nomeIndicado: string;
  telefoneIndicado: string;
  status: 'pendente' | 'em_contato' | 'convertido' | 'descartado';
  observacoes?: string;
  createdAt: string;
  updatedAt: string;
  clienteFornecedorUser: {
    id: string;
    email: string;
    clienteFornecedor: {
      id: string;
      nome: string;
      codigo: string;
      email?: string;
      telefone?: string;
      cpfCnpj?: string;
    };
  };
}

interface EmailSendLogEntry {
  id: string;
  to: string;
  subject: string;
  tipo: 'reset_password' | 'first_access';
  status: 'enviado' | 'erro';
  errorMessage?: string | null;
  createdAt: string;
}

export const ZehMotocaConfigPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    | 'usuarios'
    | 'templates'
    | 'config'
    | 'logEmails'
    | 'indicacoes'
    | 'centrais'
    | 'equipeOficina'
    | 'checklistTemplatesOficina'
    | 'agendamentoOficina'
  >('usuarios');
  const [loading, setLoading] = useState(false);

  // Estados para Usuários
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState<Usuario[]>([]);
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [showUsuarioModal, setShowUsuarioModal] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | null>(null);
  const [usuarioForm, setUsuarioForm] = useState({
    email: '',
    password: '',
    nome: '',
    cpfCnpj: '',
    telefone: '',
    clienteFornecedorId: '',
    isActive: true,
  });
  const [savingUsuario, setSavingUsuario] = useState(false);
  const [clientesFornecedores, setClientesFornecedores] = useState<any[]>([]);
  const [loadingClientesVinculo, setLoadingClientesVinculo] = useState(false);
  const [buscaClienteVinculo, setBuscaClienteVinculo] = useState('');
  const [clienteComboboxOpen, setClienteComboboxOpen] = useState(false);

  // Equipe Oficina (staff mobile)
  const [staffOficina, setStaffOficina] = useState<any[]>([]);
  const [staffForm, setStaffForm] = useState({
    companyId: '',
    nome: '',
    email: '',
    password: '',
    cpf: '',
    role: 'GERENTE_OFICINA',
  });
  const [savingStaff, setSavingStaff] = useState(false);
  const [showStaffEditModal, setShowStaffEditModal] = useState(false);
  const [staffEditing, setStaffEditing] = useState<{
    id: string;
    isActive?: boolean;
  } | null>(null);
  const [staffEditForm, setStaffEditForm] = useState({
    companyId: '',
    nome: '',
    email: '',
    cpf: '',
    role: 'GERENTE_OFICINA',
    novaSenha: '',
    isActive: true,
  });

  // Checklist Oficina (templates por empresa)
  const [checklistTemplates, setChecklistTemplates] = useState<any[]>([]);
  const [loadingChecklistTemplates, setLoadingChecklistTemplates] = useState(false);
  const [checklistTplDuplicatingId, setChecklistTplDuplicatingId] = useState<string | null>(null);
  const [selectedCompanyChecklist, setSelectedCompanyChecklist] = useState('');
  const [showChecklistTplModal, setShowChecklistTplModal] = useState(false);
  const [checklistTplSaving, setChecklistTplSaving] = useState(false);
  const [checklistTplEditingId, setChecklistTplEditingId] = useState<string | null>(null);
  const [checklistTplForm, setChecklistTplForm] = useState<{
    companyId: string;
    nome: string;
    descricao: string;
    ativo: boolean;
    grupos: ChecklistGrupoForm[];
  }>({
    companyId: '',
    nome: '',
    descricao: '',
    ativo: true,
    grupos: [emptyGrupo()],
  });

  // Estados para Templates
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    nome: '',
    tipo: 'first_access' as 'first_access' | 'reset_password',
    assunto: '',
    corpoHtml: '',
    corpoTexto: '',
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewAssunto, setPreviewAssunto] = useState('');

  // Estados para Configurações
  const [emailConfig, setEmailConfig] = useState<EmailConfig | null>(null);
  const [configForm, setConfigForm] = useState({
    provider: 'smtp' as 'smtp' | 'sendgrid',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: false,
    sendgridApiKey: '',
    fromEmail: '',
    fromName: 'ZehMotoca',
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Estados para Indicações
  const [indicacoes, setIndicacoes] = useState<Indicacao[]>([]);
  const [indicacoesFiltradas, setIndicacoesFiltradas] = useState<Indicacao[]>([]);
  const [buscaIndicacao, setBuscaIndicacao] = useState('');
  const [selectedIndicacao, setSelectedIndicacao] = useState<Indicacao | null>(null);
  const [showObservacoesModal, setShowObservacoesModal] = useState(false);
  const [observacoesText, setObservacoesText] = useState('');
  const [savingObservacoes, setSavingObservacoes] = useState(false);

  // Estados para Centrais de Atendimento (por empresa)
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [centraisList, setCentraisList] = useState<any[]>([]);
  const [selectedCompanyCentrais, setSelectedCompanyCentrais] = useState('');
  const [centraisForm, setCentraisForm] = useState({
    phoneEmergency: '',
    phoneWorkshop: '',
    phoneFinancial: '',
    phoneCustomerService: '',
    phoneTowRequest: '',
    phoneSos: '',
  });
  const [loadingCentrais, setLoadingCentrais] = useState(false);
  const [savingCentrais, setSavingCentrais] = useState(false);

  // Estados para Log de Emails
  const [logEmails, setLogEmails] = useState<EmailSendLogEntry[]>([]);
  const [logEmailsTotal, setLogEmailsTotal] = useState(0);
  const [loadingLogEmails, setLoadingLogEmails] = useState(false);
  const [logEmailsPage, setLogEmailsPage] = useState(1);
  const [logEmailsTipo, setLogEmailsTipo] = useState<string>('');
  const [logEmailsStatus, setLogEmailsStatus] = useState<string>('');
  const logEmailsLimit = 50;

  // Carregar dados
  useEffect(() => {
    if (activeTab === 'usuarios') {
      loadUsuarios();
      loadClientesFornecedores();
    } else if (activeTab === 'templates') {
      loadTemplates();
    } else if (activeTab === 'config') {
      loadEmailConfig();
    } else if (activeTab === 'indicacoes') {
      loadIndicacoes();
    } else if (activeTab === 'centrais') {
      loadCompanies();
      loadCentraisList();
    } else if (activeTab === 'equipeOficina') {
      loadStaffOficina();
      loadCompanies();
    } else if (activeTab === 'checklistTemplatesOficina') {
      loadCompanies();
    } else if (activeTab === 'agendamentoOficina') {
      loadCompanies();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'checklistTemplatesOficina' || companies.length !== 1) return;
    setSelectedCompanyChecklist((prev) => prev || companies[0]!.id);
  }, [activeTab, companies]);

  useEffect(() => {
    if (activeTab !== 'checklistTemplatesOficina') return;
    loadChecklistTemplates();
  }, [activeTab, selectedCompanyChecklist]);

  // Carregar clientes ao abrir o modal de usuário (garante lista disponível)
  useEffect(() => {
    if (showUsuarioModal) {
      loadClientesFornecedores();
    }
  }, [showUsuarioModal]);

  // Filtrar usuários
  useEffect(() => {
    if (buscaUsuario.trim()) {
      const busca = buscaUsuario.toLowerCase();
      const digits = buscaUsuario.replace(/\D/g, '');
      const filtrados = usuarios.filter((u) => {
        const textMatch =
          u.email.toLowerCase().includes(busca) ||
          u.clienteFornecedor?.nome?.toLowerCase().includes(busca) ||
          u.clienteFornecedor?.codigo?.toLowerCase().includes(busca);
        if (textMatch) return true;
        // Sem dígitos na busca: não usar .includes('') — em JS isso casa com qualquer string.
        if (digits.length === 0) return false;
        return Boolean(
          u.cpfcnpj?.includes(digits) ||
            u.clienteFornecedor?.cpfCnpj?.includes(digits) ||
            u.phone?.includes(digits) ||
            u.clienteFornecedor?.telefone?.includes(digits),
        );
      });
      setUsuariosFiltrados(filtrados);
    } else {
      setUsuariosFiltrados(usuarios);
    }
  }, [buscaUsuario, usuarios]);

  useEffect(() => {
    if (activeTab !== 'logEmails') return;
    loadLogEmails();
  }, [activeTab, logEmailsPage, logEmailsTipo, logEmailsStatus]);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const response = await api.get('/configuracoes/aplicativo-zehmotoca/usuarios', {
        params: { limit: 5000 },
      });
      setUsuarios(response.data.data || []);
      setUsuariosFiltrados(response.data.data || []);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const loadStaffOficina = async () => {
    try {
      setLoading(true);
      const response = await api.get('/configuracoes/aplicativo-zehmotoca/equipe-oficina');
      setStaffOficina(response.data.data || []);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar equipe oficina');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaffOficina = async () => {
    if (!staffForm.companyId || !staffForm.email || !staffForm.password || !staffForm.nome) {
      toast.error('Preencha empresa, nome, email e senha');
      return;
    }
    try {
      setSavingStaff(true);
      await api.post('/configuracoes/aplicativo-zehmotoca/equipe-oficina', staffForm);
      toast.success('Gerente/equipe criado');
      setStaffForm({
        companyId: '',
        nome: '',
        email: '',
        password: '',
        cpf: '',
        role: 'GERENTE_OFICINA',
      });
      await loadStaffOficina();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao criar usuário oficina');
    } finally {
      setSavingStaff(false);
    }
  };

  const openEditStaffOficina = (s: {
    id: string;
    nome: string;
    email: string;
    cpf?: string | null;
    role: string;
    companyId?: string;
    company?: { id: string; name: string };
    isActive?: boolean;
  }) => {
    setStaffEditing({ id: s.id, isActive: s.isActive !== false });
    setStaffEditForm({
      companyId: s.companyId ?? s.company?.id ?? '',
      nome: s.nome ?? '',
      email: s.email ?? '',
      cpf: s.cpf ?? '',
      role: s.role ?? 'GERENTE_OFICINA',
      novaSenha: '',
      isActive: s.isActive !== false,
    });
    setShowStaffEditModal(true);
  };

  const handleSaveStaffOficinaEdit = async () => {
    if (!staffEditing) return;
    if (!staffEditForm.companyId || !staffEditForm.email || !staffEditForm.nome) {
      toast.error('Preencha empresa, nome e e-mail');
      return;
    }
    try {
      setSavingStaff(true);
      await api.put(`/configuracoes/aplicativo-zehmotoca/equipe-oficina/${staffEditing.id}`, {
        companyId: staffEditForm.companyId,
        nome: staffEditForm.nome,
        email: staffEditForm.email,
        cpf: staffEditForm.cpf.trim() || null,
        role: staffEditForm.role,
      });
      if (staffEditForm.isActive !== staffEditing.isActive) {
        await api.patch(`/configuracoes/aplicativo-zehmotoca/equipe-oficina/${staffEditing.id}/status`, {
          isActive: staffEditForm.isActive,
        });
      }
      if (staffEditForm.novaSenha.trim().length >= 6) {
        await api.post(
          `/configuracoes/aplicativo-zehmotoca/equipe-oficina/${staffEditing.id}/resetar-senha`,
          { novaSenha: staffEditForm.novaSenha.trim() }
        );
      }
      toast.success('Usuário da equipe atualizado');
      setShowStaffEditModal(false);
      setStaffEditing(null);
      await loadStaffOficina();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar usuário oficina');
    } finally {
      setSavingStaff(false);
    }
  };

  const loadChecklistTemplates = async () => {
    try {
      setLoadingChecklistTemplates(true);
      const params =
        selectedCompanyChecklist.trim() !== ''
          ? { companyId: selectedCompanyChecklist }
          : undefined;
      const response = await api.get('/configuracoes/aplicativo-zehmotoca/checklist-templates-oficina', {
        params,
      });
      setChecklistTemplates(response.data.data || []);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar templates de checklist');
      setChecklistTemplates([]);
    } finally {
      setLoadingChecklistTemplates(false);
    }
  };

  const openNewChecklistTemplate = () => {
    setChecklistTplEditingId(null);
    setChecklistTplForm({
      companyId: selectedCompanyChecklist || companies[0]?.id || '',
      nome: '',
      descricao: '',
      ativo: true,
      grupos: [emptyGrupo()],
    });
    setShowChecklistTplModal(true);
  };

  const openEditChecklistTemplate = async (id: string) => {
    try {
      const response = await api.get(`/configuracoes/aplicativo-zehmotoca/checklist-templates-oficina/${id}`);
      const t = response.data.data;
      setChecklistTplEditingId(id);
      setChecklistTplForm({
        companyId: t.companyId,
        nome: t.nome || '',
        descricao: t.descricao ?? '',
        ativo: Boolean(t.ativo),
        grupos:
          Array.isArray(t.itens) && t.itens.length > 0
            ? itensApiToGrupos(t.itens)
            : [emptyGrupo()],
      });
      setShowChecklistTplModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar template');
    }
  };

  const handleSaveChecklistTemplate = async () => {
    if (!checklistTplForm.companyId) {
      toast.error('Selecione a empresa');
      return;
    }
    if (!checklistTplForm.nome.trim()) {
      toast.error('Informe o nome do template');
      return;
    }
    const itensPayload = flattenGruposToItens(checklistTplForm.grupos);
    if (!itensPayload.length) {
      toast.error('Adicione pelo menos um grupo, item e uma ação marcada');
      return;
    }
    try {
      setChecklistTplSaving(true);
      if (checklistTplEditingId) {
        await api.put(`/configuracoes/aplicativo-zehmotoca/checklist-templates-oficina/${checklistTplEditingId}`, {
          companyId: checklistTplForm.companyId,
          nome: checklistTplForm.nome.trim(),
          descricao: checklistTplForm.descricao.trim() || null,
          ativo: checklistTplForm.ativo,
          itens: itensPayload,
        });
        toast.success('Template atualizado');
      } else {
        await api.post('/configuracoes/aplicativo-zehmotoca/checklist-templates-oficina', {
          companyId: checklistTplForm.companyId,
          nome: checklistTplForm.nome.trim(),
          descricao: checklistTplForm.descricao.trim() || null,
          ativo: checklistTplForm.ativo,
          itens: itensPayload,
        });
        toast.success('Template criado');
      }
      setShowChecklistTplModal(false);
      await loadChecklistTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar template');
    } finally {
      setChecklistTplSaving(false);
    }
  };

  const handleToggleChecklistTemplateAtivo = async (row: any) => {
    try {
      await api.patch(`/configuracoes/aplicativo-zehmotoca/checklist-templates-oficina/${row.id}/status`, {
        ativo: !row.ativo,
      });
      toast.success(row.ativo ? 'Template desativado' : 'Template ativado');
      await loadChecklistTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleDeleteChecklistTemplate = async (row: any) => {
    if (!window.confirm(`Excluir o template "${row.nome}"? Ordens existentes podem perder a referência.`)) return;
    try {
      await api.delete(`/configuracoes/aplicativo-zehmotoca/checklist-templates-oficina/${row.id}`);
      toast.success('Template removido');
      await loadChecklistTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir');
    }
  };

  const handleDuplicateChecklistTemplate = async (row: any) => {
    try {
      setChecklistTplDuplicatingId(row.id);
      const response = await api.post(
        `/configuracoes/aplicativo-zehmotoca/checklist-templates-oficina/${row.id}/duplicate`
      );
      const copy = response.data.data;
      toast.success(`Template duplicado: "${copy?.nome ?? 'cópia'}"`);
      await loadChecklistTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao duplicar template');
    } finally {
      setChecklistTplDuplicatingId(null);
    }
  };

  const loadClientesFornecedores = async () => {
    try {
      setLoadingClientesVinculo(true);
      const response = await api.get('/clientes-fornecedores?limit=5000');
      const data = response?.data?.data ?? response?.data;
      setClientesFornecedores(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Erro ao carregar clientes/fornecedores:', error);
      toast.error(error.response?.data?.message || 'Erro ao carregar lista de clientes');
      setClientesFornecedores([]);
    } finally {
      setLoadingClientesVinculo(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/configuracoes/aplicativo-zehmotoca/templates-email');
      setTemplates(response.data.data || []);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const loadEmailConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/configuracoes/aplicativo-zehmotoca/configuracoes-email');
      if (response.data.data) {
        setEmailConfig(response.data.data);
        setConfigForm({
          provider: response.data.data.provider || 'smtp',
          smtpHost: response.data.data.smtpHost || '',
          smtpPort: response.data.data.smtpPort || 587,
          smtpUser: response.data.data.smtpUser || '',
          smtpPassword: response.data.data.smtpPassword ? '***' : '',
          smtpSecure: response.data.data.smtpSecure || false,
          sendgridApiKey: response.data.data.sendgridApiKey ? '***' : '',
          fromEmail: response.data.data.fromEmail || '',
          fromName: response.data.data.fromName || 'ZehMotoca',
        });
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || 'Erro ao carregar configurações');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLogEmails = async () => {
    try {
      setLoadingLogEmails(true);
      const params = new URLSearchParams();
      params.set('page', String(logEmailsPage));
      params.set('limit', String(logEmailsLimit));
      if (logEmailsTipo) params.set('tipo', logEmailsTipo);
      if (logEmailsStatus) params.set('status', logEmailsStatus);
      const response = await api.get(`/configuracoes/aplicativo-zehmotoca/log-emails?${params.toString()}`);
      setLogEmails(response.data.data || []);
      setLogEmailsTotal(response.data.total ?? 0);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar log de emails');
      setLogEmails([]);
      setLogEmailsTotal(0);
    } finally {
      setLoadingLogEmails(false);
    }
  };

  // Handlers de Usuários
  const handleCreateUsuario = () => {
    setSelectedUsuario(null);
    setUsuarioForm({
      email: '',
      password: '',
      nome: '',
      cpfCnpj: '',
      telefone: '',
      clienteFornecedorId: '',
      isActive: true,
    });
    setBuscaClienteVinculo('');
    setClienteComboboxOpen(false);
    setShowUsuarioModal(true);
  };

  const handleEditUsuario = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setUsuarioForm({
      email: usuario.email,
      password: '',
      nome: usuario.clienteFornecedor?.nome || '',
      cpfCnpj: usuario.cpfcnpj || usuario.clienteFornecedor?.cpfCnpj || '',
      telefone: usuario.phone || usuario.clienteFornecedor?.telefone || '',
      clienteFornecedorId: usuario.clienteFornecedor?.id || '',
      isActive: usuario.isActive,
    });
    setBuscaClienteVinculo('');
    setClienteComboboxOpen(false);
    setShowUsuarioModal(true);
  };

  const handleSaveUsuario = async () => {
    try {
      setSavingUsuario(true);
      if (selectedUsuario) {
        await api.put(`/configuracoes/aplicativo-zehmotoca/usuarios/${selectedUsuario.id}`, {
          email: usuarioForm.email,
          nome: usuarioForm.nome || undefined,
          telefone: usuarioForm.telefone || undefined,
          cpfCnpj: usuarioForm.cpfCnpj || undefined,
          // Sempre enviar vínculo: id do cliente ou null para desvincular
          clienteFornecedorId: usuarioForm.clienteFornecedorId === '' ? null : usuarioForm.clienteFornecedorId || null,
          ...(usuarioForm.password && { password: usuarioForm.password }),
        });
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await api.post('/configuracoes/aplicativo-zehmotoca/usuarios', {
          email: usuarioForm.email,
          password: usuarioForm.password,
          nome: usuarioForm.nome || undefined,
          cpfCnpj: usuarioForm.cpfCnpj || undefined,
          telefone: usuarioForm.telefone || undefined,
          clienteFornecedorId: usuarioForm.clienteFornecedorId || undefined,
          isActive: usuarioForm.isActive,
        });
        toast.success('Usuário criado com sucesso!');
      }
      setShowUsuarioModal(false);
      loadUsuarios();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar usuário');
    } finally {
      setSavingUsuario(false);
    }
  };

  const handleToggleUsuarioStatus = async (usuario: Usuario) => {
    try {
      await api.patch(`/configuracoes/aplicativo-zehmotoca/usuarios/${usuario.id}/status`, {
        isActive: !usuario.isActive,
      });
      toast.success(`Usuário ${usuario.isActive ? 'desativado' : 'ativado'} com sucesso!`);
      loadUsuarios();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleResetPassword = async (usuario: Usuario) => {
    if (!confirm('Deseja realmente resetar a senha deste usuário?')) return;
    try {
      await api.post(`/configuracoes/aplicativo-zehmotoca/usuarios/${usuario.id}/resetar-senha`);
      toast.success('Senha resetada com sucesso! Nova senha enviada por email.');
      loadUsuarios();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao resetar senha');
    }
  };

  // Handlers de Templates
  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setTemplateForm({
      nome: '',
      tipo: 'first_access',
      assunto: '',
      corpoHtml: '',
      corpoTexto: '',
    });
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setTemplateForm({
      nome: template.nome,
      tipo: template.tipo,
      assunto: template.assunto,
      corpoHtml: template.corpoHtml,
      corpoTexto: template.corpoTexto || '',
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    try {
      setSavingTemplate(true);
      if (selectedTemplate) {
        await api.put(`/configuracoes/aplicativo-zehmotoca/templates-email/${selectedTemplate.id}`, templateForm);
        toast.success('Template atualizado com sucesso!');
      } else {
        await api.post('/configuracoes/aplicativo-zehmotoca/templates-email', templateForm);
        toast.success('Template criado com sucesso!');
      }
      setShowTemplateModal(false);
      loadTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (template: EmailTemplate) => {
    if (!confirm('Deseja realmente deletar este template?')) return;
    try {
      await api.delete(`/configuracoes/aplicativo-zehmotoca/templates-email/${template.id}`);
      toast.success('Template deletado com sucesso!');
      loadTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao deletar template');
    }
  };

  const handleToggleTemplateStatus = async (template: EmailTemplate) => {
    try {
      await api.patch(`/configuracoes/aplicativo-zehmotoca/templates-email/${template.id}/status`, {
        isActive: !template.isActive,
      });
      toast.success(`Template ${template.isActive ? 'desativado' : 'ativado'} com sucesso!`);
      loadTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handlePreviewTemplate = async (template: EmailTemplate) => {
    try {
      const response = await api.post(`/configuracoes/aplicativo-zehmotoca/templates-email/${template.id}/preview`, {
        dadosExemplo: { nome: 'João Silva', codigo: '123456', tempo_expiracao: '15 minutos' },
      });
      const data = response.data?.data ?? response.data;
      setPreviewAssunto(data.assunto || template.assunto || '');
      setPreviewContent(data.corpoHtml || data.html || '');
      setShowPreviewModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao gerar preview');
    }
  };

  /** Preview a partir do conteúdo atual do formulário (novo ou em edição), sem chamar API */
  const handlePreviewFromForm = () => {
    const exemplo = { nome: 'João Silva', codigo: '123456', tempo_expiracao: '15 minutos' };
    let html = templateForm.corpoHtml || '';
    ['nome', 'codigo', 'tempo_expiracao'].forEach((key) => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
      html = html.replace(regex, (exemplo as Record<string, string>)[key] ?? `{{${key}}}`);
    });
    setPreviewAssunto(templateForm.assunto || '(Sem assunto)');
    setPreviewContent(html || '<p class="text-muted-foreground">Nenhum conteúdo HTML definido.</p>');
    setShowPreviewModal(true);
  };

  // Handlers de Configurações
  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);
      await api.post('/configuracoes/aplicativo-zehmotoca/configuracoes-email', {
        ...configForm,
        isActive: true,
      });
      toast.success('Configurações salvas com sucesso!');
      loadEmailConfig();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setSavingConfig(false);
    }
  };

  const applyGmailPreset = (preset: typeof GMAIL_SMTP_PRESET_587 | typeof GMAIL_SMTP_PRESET_465) => {
    setConfigForm((prev) => ({
      ...prev,
      provider: 'smtp',
      smtpHost: preset.smtpHost,
      smtpPort: preset.smtpPort,
      smtpSecure: preset.smtpSecure,
      fromEmail: prev.fromEmail || prev.smtpUser || '',
    }));
    toast.success(
      preset.smtpPort === 587
        ? 'Preset Gmail: porta 587 (STARTTLS — deixe "Conexão segura" desmarcada)'
        : 'Preset Gmail: porta 465 (SSL — "Conexão segura" marcada)'
    );
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      const response = await api.post('/configuracoes/aplicativo-zehmotoca/configuracoes-email/testar', configForm);
      toast.success(response.data.message || 'Conexão testada com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao testar conexão');
    } finally {
      setTestingConnection(false);
    }
  };

  // Handlers de Indicações
  const loadIndicacoes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/configuracoes/aplicativo-zehmotoca/indicacoes', {
        params: { limit: 5000 },
      });
      const data = response.data.data || [];
      setIndicacoes(data);
      setIndicacoesFiltradas(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar indicações');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar indicações
  useEffect(() => {
    if (buscaIndicacao.trim()) {
      const busca = buscaIndicacao.toLowerCase();
      const digits = buscaIndicacao.replace(/\D/g, '');
      const filtradas = indicacoes.filter((i) => {
        const textMatch =
          i.nomeIndicado.toLowerCase().includes(busca) ||
          i.clienteFornecedorUser.clienteFornecedor.nome.toLowerCase().includes(busca);
        if (textMatch) return true;
        if (digits.length === 0) return false;
        return i.telefoneIndicado.includes(digits);
      });
      setIndicacoesFiltradas(filtradas);
    } else {
      setIndicacoesFiltradas(indicacoes);
    }
  }, [buscaIndicacao, indicacoes]);

  const handleUpdateStatus = async (indicacao: Indicacao, newStatus: 'pendente' | 'em_contato' | 'convertido' | 'descartado') => {
    try {
      await api.patch(`/configuracoes/aplicativo-zehmotoca/indicacoes/${indicacao.id}/status`, { status: newStatus });
      toast.success('Status atualizado com sucesso!');
      loadIndicacoes();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar status');
    }
  };

  const handleOpenObservacoes = (indicacao: Indicacao) => {
    setSelectedIndicacao(indicacao);
    setObservacoesText(indicacao.observacoes || '');
    setShowObservacoesModal(true);
  };

  const handleSaveObservacoes = async () => {
    if (!selectedIndicacao) return;
    try {
      setSavingObservacoes(true);
      await api.patch(`/configuracoes/aplicativo-zehmotoca/indicacoes/${selectedIndicacao.id}/observacoes`, {
        observacoes: observacoesText,
      });
      toast.success('Observações salvas com sucesso!');
      setShowObservacoesModal(false);
      loadIndicacoes();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar observações');
    } finally {
      setSavingObservacoes(false);
    }
  };

  // Centrais de atendimento (por empresa)
  const loadCompanies = async () => {
    try {
      const response = await api.get('/companies?isActive=true');
      const data = response?.data?.data;
      const list = Array.isArray(data) ? data : (data?.companies ?? []);
      setCompanies(list.map((c: any) => ({ id: c.id, name: c.name || c.nome || '' })));
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao carregar empresas');
    }
  };

  const loadCentraisList = async () => {
    try {
      setLoadingCentrais(true);
      const response = await api.get('/configuracoes/aplicativo-zehmotoca/centrais');
      setCentraisList(response.data.data || []);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao carregar centrais');
      setCentraisList([]);
    } finally {
      setLoadingCentrais(false);
    }
  };

  const loadCentraisByCompany = async (companyId: string) => {
    if (!companyId) {
      setCentraisForm({ phoneEmergency: '', phoneWorkshop: '', phoneFinancial: '', phoneCustomerService: '', phoneTowRequest: '', phoneSos: '' });
      return;
    }
    try {
      const response = await api.get(`/configuracoes/aplicativo-zehmotoca/centrais/${companyId}`);
      const d = response.data.data || {};
      setCentraisForm({
        phoneEmergency: d.phoneEmergency ?? '',
        phoneWorkshop: d.phoneWorkshop ?? '',
        phoneFinancial: d.phoneFinancial ?? '',
        phoneCustomerService: d.phoneCustomerService ?? '',
        phoneTowRequest: d.phoneTowRequest ?? '',
        phoneSos: d.phoneSos ?? '',
      });
    } catch {
      setCentraisForm({ phoneEmergency: '', phoneWorkshop: '', phoneFinancial: '', phoneCustomerService: '', phoneTowRequest: '', phoneSos: '' });
    }
  };

  useEffect(() => {
    if (activeTab === 'centrais' && selectedCompanyCentrais) {
      loadCentraisByCompany(selectedCompanyCentrais);
    } else if (!selectedCompanyCentrais) {
      setCentraisForm({ phoneEmergency: '', phoneWorkshop: '', phoneFinancial: '', phoneCustomerService: '', phoneTowRequest: '', phoneSos: '' });
    }
  }, [activeTab, selectedCompanyCentrais]);

  const handleSaveCentrais = async () => {
    if (!selectedCompanyCentrais) {
      toast.error('Selecione uma empresa');
      return;
    }
    try {
      setSavingCentrais(true);
      await api.put('/configuracoes/aplicativo-zehmotoca/centrais', {
        companyId: selectedCompanyCentrais,
        phoneEmergency: centraisForm.phoneEmergency.trim() || null,
        phoneWorkshop: centraisForm.phoneWorkshop.trim() || null,
        phoneFinancial: centraisForm.phoneFinancial.trim() || null,
        phoneCustomerService: centraisForm.phoneCustomerService.trim() || null,
        phoneTowRequest: centraisForm.phoneTowRequest.trim() || null,
        phoneSos: centraisForm.phoneSos.trim() || null,
      });
      toast.success('Telefones das centrais salvos com sucesso');
      loadCentraisList();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSavingCentrais(false);
    }
  };

  // Agrupar indicações por status
  const indicacoesPorStatus = {
    pendente: indicacoesFiltradas.filter(i => i.status === 'pendente'),
    em_contato: indicacoesFiltradas.filter(i => i.status === 'em_contato'),
    convertido: indicacoesFiltradas.filter(i => i.status === 'convertido'),
    descartado: indicacoesFiltradas.filter(i => i.status === 'descartado'),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Smartphone className="w-8 h-8" />
            Aplicativo ZehMotoca
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie usuários, templates de email e configurações do servidor
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('usuarios')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'usuarios'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Usuários
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Templates de Email
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'config'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Configurações de Email
          </button>
          <button
            onClick={() => setActiveTab('logEmails')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logEmails'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Log de Emails
          </button>
          <button
            onClick={() => setActiveTab('indicacoes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'indicacoes'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <Kanban className="w-4 h-4 inline mr-2" />
            Indicações
          </button>
          <button
            onClick={() => setActiveTab('centrais')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'centrais'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <PhoneCall className="w-4 h-4 inline mr-2" />
            Centrais de Atendimento
          </button>
          <button
            onClick={() => setActiveTab('equipeOficina')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'equipeOficina'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Equipe Oficina
          </button>
          <button
            onClick={() => setActiveTab('checklistTemplatesOficina')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'checklistTemplatesOficina'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <ClipboardList className="w-4 h-4 inline mr-2" />
            Checklist Oficina
          </button>
          <button
            onClick={() => setActiveTab('agendamentoOficina')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'agendamentoOficina'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
            }`}
          >
            <CalendarClock className="w-4 h-4 inline mr-2" />
            Agendamento Oficina
          </button>
        </nav>
      </div>

      {/* Conteúdo das Abas */}
      {activeTab === 'usuarios' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Buscar por email ou nome..."
                  value={buscaUsuario}
                  onChange={(e) => setBuscaUsuario(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button onClick={handleCreateUsuario}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : (
            <>
              <ZehMotocaUsuariosTable
                data={usuariosFiltrados}
                onEdit={handleEditUsuario}
                onToggleStatus={handleToggleUsuarioStatus}
                onResetPassword={handleResetPassword}
              />
              {usuariosFiltrados.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {usuariosFiltrados.length} usuário(s)
                  {buscaUsuario.trim() ? ' (filtrados)' : ''}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={handleCreateTemplate}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Template
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : (
            <div className="grid gap-4">
              {templates.map((template) => (
                <Card key={template.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{template.nome}</h3>
                          <Badge>{template.tipo === 'first_access' ? 'Primeiro Acesso' : 'Recuperação de Senha'}</Badge>
                          <Badge variant={template.isActive ? 'default' : 'secondary'}>
                            {template.isActive ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{template.assunto}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreviewTemplate(template)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditTemplate(template)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleTemplateStatus(template)}
                        >
                          {template.isActive ? (
                            <PowerOff className="w-4 h-4 text-red-500" />
                          ) : (
                            <Power className="w-4 h-4 text-green-500" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteTemplate(template)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {templates.length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Nenhum template encontrado</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configurações do Servidor de Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Provedor</label>
                <Select
                  value={configForm.provider}
                  onValueChange={(value) => setConfigForm({ ...configForm, provider: value as 'smtp' | 'sendgrid' })}
                >
                  <option value="smtp">SMTP</option>
                  <option value="sendgrid">SendGrid</option>
                </Select>
              </div>

              {configForm.provider === 'smtp' ? (
                <>
                  <div className="rounded-lg border border-blue-200 bg-blue-50/80 p-4 dark:border-blue-800 dark:bg-blue-950/40">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Gmail / Google Workspace</p>
                    <p className="mt-1 text-xs text-blue-800/90 dark:text-blue-200/90">
                      Host <strong>smtp.gmail.com</strong> (não use email-ssl.com.br). Usuário = e-mail completo (
                      ex. comercial@zehmotoca.com). Senha = <strong>senha de app</strong> de 16 caracteres (Google
                      Conta → Segurança → Verificação em 2 etapas → Senhas de app). Remetente = mesmo e-mail do usuário.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => applyGmailPreset(GMAIL_SMTP_PRESET_587)}>
                        Usar Gmail (587 — recomendado)
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => applyGmailPreset(GMAIL_SMTP_PRESET_465)}>
                        Usar Gmail (465 — SSL)
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Host SMTP</label>
                      <Input
                        value={configForm.smtpHost}
                        onChange={(e) => {
                          const smtpHost = normalizeSmtpHostInput(e.target.value);
                          const updates: Partial<typeof configForm> = { smtpHost };
                          if (isGmailSmtpHost(smtpHost)) {
                            updates.smtpPort = 587;
                            updates.smtpSecure = false;
                          }
                          setConfigForm({ ...configForm, ...updates });
                        }}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Porta</label>
                      <Input
                        type="number"
                        value={configForm.smtpPort}
                        onChange={(e) => setConfigForm({ ...configForm, smtpPort: parseInt(e.target.value) })}
                        placeholder="587"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Usuário</label>
                    <Input
                      value={configForm.smtpUser}
                      onChange={(e) => setConfigForm({ ...configForm, smtpUser: e.target.value })}
                      placeholder="seu-email@gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Senha</label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={configForm.smtpPassword}
                        onChange={(e) => setConfigForm({ ...configForm, smtpPassword: e.target.value })}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2"
                      >
                        {showPassword ? <X className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={configForm.smtpSecure}
                        onChange={(e) => setConfigForm({ ...configForm, smtpSecure: e.target.checked })}
                      />
                      <span className="text-sm font-medium">Conexão Segura (TLS/SSL)</span>
                    </label>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Porta <strong>587</strong> ou <strong>2525</strong>: deixe desmarcado (STARTTLS). Porta{' '}
                      <strong>465</strong>: marque (SSL implícito). Marcar em 587 costuma gerar erro de TLS no servidor.
                      {isGmailSmtpHost(configForm.smtpHost) && (
                        <span className="mt-1 block text-blue-700 dark:text-blue-300">
                          Gmail detectado: use os botões acima ou porta 587 sem SSL.
                        </span>
                      )}
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2">API Key SendGrid</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={configForm.sendgridApiKey}
                      onChange={(e) => setConfigForm({ ...configForm, sendgridApiKey: e.target.value })}
                      placeholder="SG.xxxxxxxxxxxxx"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2"
                    >
                      {showPassword ? <X className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email Remetente</label>
                  <Input
                    type="email"
                    value={configForm.fromEmail}
                    onChange={(e) => setConfigForm({ ...configForm, fromEmail: e.target.value })}
                    placeholder="noreply@zehmotoca.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Nome Remetente</label>
                  <Input
                    value={configForm.fromName}
                    onChange={(e) => setConfigForm({ ...configForm, fromName: e.target.value })}
                    placeholder="ZehMotoca"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4">
                <Button onClick={handleSaveConfig} loading={savingConfig}>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Configurações
                </Button>
                <Button variant="outline" onClick={handleTestConnection} loading={testingConnection}>
                  <TestTube className="w-4 h-4 mr-2" />
                  Testar Conexão
                </Button>
              </div>

              {emailConfig && emailConfig.isActive && (
                <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <span className="text-sm text-green-800 dark:text-green-200">
                    Configuração ativa e funcionando
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'logEmails' && (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Histórico de envio de emails do sistema (recuperação de senha e primeiro acesso).
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Tipo</label>
              <select
                value={logEmailsTipo}
                onChange={(e) => {
                  setLogEmailsTipo(e.target.value);
                  setLogEmailsPage(1);
                }}
                className="border rounded-md px-3 py-2 text-sm bg-background"
              >
                <option value="">Todos</option>
                <option value="reset_password">Recuperação de senha</option>
                <option value="first_access">Primeiro acesso</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Status</label>
              <select
                value={logEmailsStatus}
                onChange={(e) => {
                  setLogEmailsStatus(e.target.value);
                  setLogEmailsPage(1);
                }}
                className="border rounded-md px-3 py-2 text-sm bg-background"
              >
                <option value="">Todos</option>
                <option value="enviado">Enviado</option>
                <option value="erro">Erro</option>
              </select>
            </div>
            <Button variant="outline" onClick={loadLogEmails} disabled={loadingLogEmails}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingLogEmails ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>

          {loadingLogEmails ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">Data/Hora</th>
                      <th className="text-left p-3 font-medium">Destinatário</th>
                      <th className="text-left p-3 font-medium">Assunto</th>
                      <th className="text-left p-3 font-medium">Tipo</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logEmails.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          Nenhum registro de envio encontrado.
                        </td>
                      </tr>
                    ) : (
                      logEmails.map((entry) => (
                        <tr key={entry.id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="p-3 whitespace-nowrap">
                            {new Date(entry.createdAt).toLocaleString('pt-BR')}
                          </td>
                          <td className="p-3">{entry.to}</td>
                          <td className="p-3 max-w-xs truncate" title={entry.subject}>
                            {entry.subject}
                          </td>
                          <td className="p-3">
                            {entry.tipo === 'reset_password' ? 'Recuperação de senha' : 'Primeiro acesso'}
                          </td>
                          <td className="p-3">
                            <Badge variant={entry.status === 'enviado' ? 'default' : 'destructive'}>
                              {entry.status === 'enviado' ? 'Enviado' : 'Erro'}
                            </Badge>
                          </td>
                          <td className="p-3 text-muted-foreground max-w-xs truncate" title={entry.errorMessage ?? ''}>
                            {entry.errorMessage ?? '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {logEmailsTotal > logEmailsLimit && (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm text-muted-foreground">
                    {logEmails.length} de {logEmailsTotal} registros
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={logEmailsPage <= 1}
                      onClick={() => setLogEmailsPage((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={logEmailsPage * logEmailsLimit >= logEmailsTotal}
                      onClick={() => setLogEmailsPage((p) => p + 1)}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'indicacoes' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Buscar por nome ou telefone..."
                  value={buscaIndicacao}
                  onChange={(e) => setBuscaIndicacao(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button onClick={loadIndicacoes} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-4">
              {/* Coluna: Pendente */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">Pendente</h3>
                  <Badge variant="secondary">{indicacoesPorStatus.pendente.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[400px]">
                  {indicacoesPorStatus.pendente.map((indicacao) => (
                    <Card key={indicacao.id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <IndicacaoCard 
                          indicacao={indicacao} 
                          onUpdateStatus={handleUpdateStatus}
                          onOpenObservacoes={handleOpenObservacoes}
                        />
                      </CardContent>
                    </Card>
                  ))}
                  {indicacoesPorStatus.pendente.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhuma indicação pendente
                    </div>
                  )}
                </div>
              </div>

              {/* Coluna: Em Contato */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 className="font-semibold text-blue-800 dark:text-blue-200">Em Contato</h3>
                  <Badge variant="secondary">{indicacoesPorStatus.em_contato.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[400px]">
                  {indicacoesPorStatus.em_contato.map((indicacao) => (
                    <Card key={indicacao.id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <IndicacaoCard 
                          indicacao={indicacao} 
                          onUpdateStatus={handleUpdateStatus}
                          onOpenObservacoes={handleOpenObservacoes}
                        />
                      </CardContent>
                    </Card>
                  ))}
                  {indicacoesPorStatus.em_contato.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhuma indicação em contato
                    </div>
                  )}
                </div>
              </div>

              {/* Coluna: Convertido */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <h3 className="font-semibold text-green-800 dark:text-green-200">Convertido</h3>
                  <Badge variant="secondary">{indicacoesPorStatus.convertido.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[400px]">
                  {indicacoesPorStatus.convertido.map((indicacao) => (
                    <Card key={indicacao.id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <IndicacaoCard 
                          indicacao={indicacao} 
                          onUpdateStatus={handleUpdateStatus}
                          onOpenObservacoes={handleOpenObservacoes}
                        />
                      </CardContent>
                    </Card>
                  ))}
                  {indicacoesPorStatus.convertido.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhuma indicação convertida
                    </div>
                  )}
                </div>
              </div>

              {/* Coluna: Descartado */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">Descartado</h3>
                  <Badge variant="secondary">{indicacoesPorStatus.descartado.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[400px]">
                  {indicacoesPorStatus.descartado.map((indicacao) => (
                    <Card key={indicacao.id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <IndicacaoCard 
                          indicacao={indicacao} 
                          onUpdateStatus={handleUpdateStatus}
                          onOpenObservacoes={handleOpenObservacoes}
                        />
                      </CardContent>
                    </Card>
                  ))}
                  {indicacoesPorStatus.descartado.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhuma indicação descartada
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'centrais' && (
        <div className="space-y-6">
          <p className="text-muted-foreground">
            Cadastre os telefones das centrais de atendimento por empresa (incluindo Guincho e SOS da tela inicial do app). No app, os números seguem a empresa da locação.
          </p>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PhoneCall className="w-5 h-5" />
                Telefones por empresa
              </CardTitle>
              
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Empresa</label>
                <Select
                  value={selectedCompanyCentrais}
                  onValueChange={(v) => setSelectedCompanyCentrais(v)}
                  aria-label="Selecionar empresa"
                >
                  <option value="">Selecione uma empresa</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </div>
              {selectedCompanyCentrais && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Central de Emergência</label>
                      <Input
                        value={centraisForm.phoneEmergency}
                        onChange={(e) => setCentraisForm((f) => ({ ...f, phoneEmergency: e.target.value }))}
                        placeholder="Ex: 0800 201 9090"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Oficina</label>
                      <Input
                        value={centraisForm.phoneWorkshop}
                        onChange={(e) => setCentraisForm((f) => ({ ...f, phoneWorkshop: e.target.value }))}
                        placeholder="Ex: 84 98131 2924"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Atendimento ao Cliente</label>
                      <Input
                        value={centraisForm.phoneCustomerService}
                        onChange={(e) => setCentraisForm((f) => ({ ...f, phoneCustomerService: e.target.value }))}
                        placeholder="Ex: 84 3026-3950"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Financeiro</label>
                      <Input
                        value={centraisForm.phoneFinancial}
                        onChange={(e) => setCentraisForm((f) => ({ ...f, phoneFinancial: e.target.value }))}
                        placeholder="Ex: 11 91197 5992"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Solicitar Guincho (tela inicial)</label>
                      <Input
                        value={centraisForm.phoneTowRequest}
                        onChange={(e) => setCentraisForm((f) => ({ ...f, phoneTowRequest: e.target.value }))}
                        placeholder="WhatsApp — ex: 84 99999 0000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">SOS / Central 24h (tela inicial)</label>
                      <Input
                        value={centraisForm.phoneSos}
                        onChange={(e) => setCentraisForm((f) => ({ ...f, phoneSos: e.target.value }))}
                        placeholder="WhatsApp — ex: 84 99999 0001"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleSaveCentrais} disabled={savingCentrais}>
                      {savingCentrais ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Salvar telefones
                    </Button>
                  </div>
                </>
              )}
              {loadingCentrais && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Carregando...
                </p>
              )}
              {centraisList.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-medium mb-2">Resumo por empresa</h4>
                  <div className="rounded-md border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">Empresa</th>
                          <th className="text-left p-2">Emergência</th>
                          <th className="text-left p-2">Oficina</th>
                          <th className="text-left p-2">Atendimento</th>
                          <th className="text-left p-2">Financeiro</th>
                          <th className="text-left p-2">Guincho</th>
                          <th className="text-left p-2">SOS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {centraisList.map((row: any) => (
                          <tr key={row.companyId} className="border-t">
                            <td className="p-2 font-medium">{row.company?.name ?? row.companyId}</td>
                            <td className="p-2 text-muted-foreground">{row.phoneEmergency || '—'}</td>
                            <td className="p-2 text-muted-foreground">{row.phoneWorkshop || '—'}</td>
                            <td className="p-2 text-muted-foreground">{row.phoneCustomerService || '—'}</td>
                            <td className="p-2 text-muted-foreground">{row.phoneFinancial || '—'}</td>
                            <td className="p-2 text-muted-foreground">{row.phoneTowRequest || '—'}</td>
                            <td className="p-2 text-muted-foreground">{row.phoneSos || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'equipeOficina' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Novo usuário staff</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Empresa</label>
                <Select value={staffForm.companyId} onValueChange={(v) => setStaffForm((f) => ({ ...f, companyId: v }))}>
                  <option value="">Selecione</option>
                  {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Nome</label>
                <Input value={staffForm.nome} onChange={(e) => setStaffForm((f) => ({ ...f, nome: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">E-mail</label>
                <Input type="email" value={staffForm.email} onChange={(e) => setStaffForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Senha</label>
                <Input type="password" value={staffForm.password} onChange={(e) => setStaffForm((f) => ({ ...f, password: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  CPF (obrigatório para inspetor, consultor e técnico — ERP)
                </label>
                <Input
                  value={staffForm.cpf}
                  onChange={(e) => setStaffForm((f) => ({ ...f, cpf: e.target.value }))}
                  placeholder="Somente números"
                  inputMode="numeric"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Perfil (app Oficina)</label>
                <Select
                  value={staffForm.role}
                  onValueChange={(v) => setStaffForm((f) => ({ ...f, role: v }))}
                >
                  <option value="GERENTE_OFICINA">Gerente da oficina</option>
                  <option value="ADMIN_OFICINA">Admin oficina</option>
                  <option value="RECEPCIONISTA">Recepcionista (recepção, placa, reclamação)</option>
                  <option value="INSPETOR">Inspetor (abertura OS, KM, fotos, checklist, diagnóstico)</option>
                  <option value="TECNICO">Técnico</option>
                  <option value="CONSULTOR">Consultor</option>
                  <option value="MECANICO">Mecânico (consulta)</option>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Button onClick={handleCreateStaffOficina} disabled={savingStaff}>
                  {savingStaff ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Criar usuário
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Equipe cadastrada</CardTitle></CardHeader>
            <CardContent>
              {staffOficina.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum usuário.</p>
              ) : (
                <table className="w-full text-sm border rounded-md">
                  <thead>
                    <tr>
                      <th className="p-2 text-left">Nome</th>
                      <th className="p-2 text-left">CPF</th>
                      <th className="p-2 text-left">E-mail</th>
                      <th className="p-2 text-left">Perfil</th>
                      <th className="p-2 text-left">Empresa</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffOficina.map((s: any) => (
                      <tr key={s.id} className="border-t">
                        <td className="p-2">{s.nome}</td>
                        <td className="p-2 text-muted-foreground">{s.cpf || '—'}</td>
                        <td className="p-2">{s.email}</td>
                        <td className="p-2">{s.role}</td>
                        <td className="p-2">{s.company?.name}</td>
                        <td className="p-2">
                          <Badge variant={s.isActive !== false ? 'default' : 'secondary'}>
                            {s.isActive !== false ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td className="p-2 text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openEditStaffOficina(s)}
                            aria-label={`Editar ${s.nome}`}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Modal
        isOpen={showStaffEditModal}
        onClose={() => {
          setShowStaffEditModal(false);
          setStaffEditing(null);
        }}
        title="Editar usuário da equipe oficina"
        size="lg"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Empresa</label>
            <Select
              value={staffEditForm.companyId}
              onValueChange={(v) => setStaffEditForm((f) => ({ ...f, companyId: v }))}
            >
              <option value="">Selecione</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Nome</label>
            <Input
              value={staffEditForm.nome}
              onChange={(e) => setStaffEditForm((f) => ({ ...f, nome: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">E-mail</label>
            <Input
              type="email"
              value={staffEditForm.email}
              onChange={(e) => setStaffEditForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">
              CPF (obrigatório para inspetor, consultor e técnico — ERP)
            </label>
            <Input
              value={staffEditForm.cpf}
              onChange={(e) => setStaffEditForm((f) => ({ ...f, cpf: e.target.value }))}
              placeholder="Somente números"
              inputMode="numeric"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Perfil (app Oficina)</label>
            <Select
              value={staffEditForm.role}
              onValueChange={(v) => setStaffEditForm((f) => ({ ...f, role: v }))}
            >
              <option value="GERENTE_OFICINA">Gerente da oficina</option>
              <option value="ADMIN_OFICINA">Admin oficina</option>
              <option value="RECEPCIONISTA">Recepcionista</option>
              <option value="INSPETOR">Inspetor</option>
              <option value="TECNICO">Técnico</option>
              <option value="CONSULTOR">Consultor</option>
              <option value="MECANICO">Mecânico</option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Nova senha (opcional)</label>
            <Input
              type="password"
              value={staffEditForm.novaSenha}
              onChange={(e) => setStaffEditForm((f) => ({ ...f, novaSenha: e.target.value }))}
              placeholder="Mín. 6 caracteres — deixe vazio para não alterar"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <Checkbox
              id="staff-edit-ativo"
              checked={staffEditForm.isActive}
              onChange={() => setStaffEditForm((f) => ({ ...f, isActive: !f.isActive }))}
            />
            <label htmlFor="staff-edit-ativo" className="text-sm">
              Usuário ativo (pode entrar no app Oficina)
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowStaffEditModal(false);
              setStaffEditing(null);
            }}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={() => void handleSaveStaffOficinaEdit()} disabled={savingStaff}>
            {savingStaff ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar
          </Button>
        </div>
      </Modal>

      {activeTab === 'checklistTemplatesOficina' && (
        <div className="space-y-6">
          <p className="text-muted-foreground">
            Modelos de checklist: Grupo → Item → ações permitidas (OK, Inspecionar, Ajustar, Trocar, Urgente).
          </p>
          <Card>
            <CardHeader>
              <CardTitle>Filtro e ações</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="flex-1 max-w-md">
                <label className="block text-sm font-medium mb-1.5">Empresa (opcional para admin — vazio lista todas)</label>
                <Select
                  value={selectedCompanyChecklist}
                  onValueChange={(v) => setSelectedCompanyChecklist(v)}
                >
                  <option value="">Todas / selecione</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              <Button onClick={openNewChecklistTemplate}>
                <Plus className="w-4 h-4 mr-2" />
                Novo template
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Templates cadastrados</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingChecklistTemplates ? (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Carregando...
                </p>
              ) : checklistTemplates.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum template para o filtro atual.</p>
              ) : (
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 text-left">Empresa</th>
                        <th className="p-2 text-left">Nome</th>
                        <th className="p-2 text-left">Itens</th>
                        <th className="p-2 text-left">Ativo</th>
                        <th className="p-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checklistTemplates.map((t: any) => (
                        <tr key={t.id} className="border-t">
                          <td className="p-2">{t.companyName ?? '—'}</td>
                          <td className="p-2 font-medium">{t.nome}</td>
                          <td className="p-2 text-muted-foreground">{Array.isArray(t.itens) ? t.itens.length : 0}</td>
                          <td className="p-2">
                            <Badge variant={t.ativo ? 'default' : 'secondary'}>{t.ativo ? 'Sim' : 'Não'}</Badge>
                          </td>
                          <td className="p-2 text-right space-x-1 whitespace-nowrap">
                            <Button variant="outline" size="sm" onClick={() => openEditChecklistTemplate(t.id)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              title="Duplicar template"
                              disabled={checklistTplDuplicatingId === t.id}
                              onClick={() => void handleDuplicateChecklistTemplate(t)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleToggleChecklistTemplateAtivo(t)}>
                              {t.ativo ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDeleteChecklistTemplate(t)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'agendamentoOficina' && (
        <ZehMotocaAgendamentoTabs companies={companies} />
      )}

      <Modal
        isOpen={showChecklistTplModal}
        onClose={() => setShowChecklistTplModal(false)}
        title={checklistTplEditingId ? 'Editar template de checklist' : 'Novo template de checklist'}
        size="lg"
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <label className="block text-sm font-medium mb-1.5">Empresa</label>
            <Select
              value={checklistTplForm.companyId}
              onValueChange={(v) => setChecklistTplForm((f) => ({ ...f, companyId: v }))}
            >
              <option value="">Selecione</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Nome do template</label>
            <Input
              value={checklistTplForm.nome}
              onChange={(e) => setChecklistTplForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Ex: Revisão pré-venda"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Descrição (opcional)</label>
            <Textarea
              value={checklistTplForm.descricao}
              onChange={(e) => setChecklistTplForm((f) => ({ ...f, descricao: e.target.value }))}
              rows={2}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="chk-tpl-ativo"
              checked={checklistTplForm.ativo}
              onChange={() => setChecklistTplForm((f) => ({ ...f, ativo: !f.ativo }))}
            />
            <label htmlFor="chk-tpl-ativo" className="text-sm">
              Template ativo (visível no app)
            </label>
          </div>
          <ChecklistTemplateGruposEditor
            grupos={checklistTplForm.grupos}
            onChange={(grupos) => setChecklistTplForm((f) => ({ ...f, grupos }))}
          />
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={() => setShowChecklistTplModal(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleSaveChecklistTemplate} disabled={checklistTplSaving}>
              {checklistTplSaving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Usuário (Novo / Edição) */}
      <Modal
        isOpen={showUsuarioModal}
        onClose={() => setShowUsuarioModal(false)}
        title={selectedUsuario ? 'Editar usuário do app' : 'Novo usuário do app'}
        size="lg"
      >
        <div className="space-y-6">
          {/* Dados de acesso */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Dados de acesso
            </h3>
            <div>
              <label className="block text-sm font-medium mb-1.5">Email <span className="text-red-500">*</span></label>
              <Input
                type="email"
                value={usuarioForm.email}
                onChange={(e) => setUsuarioForm({ ...usuarioForm, email: e.target.value })}
                placeholder="usuario@exemplo.com"
                required
                aria-required="true"
              />
            </div>
            {!selectedUsuario && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Senha <span className="text-red-500">*</span></label>
                <Input
                  type="password"
                  value={usuarioForm.password}
                  onChange={(e) => setUsuarioForm({ ...usuarioForm, password: e.target.value })}
                  placeholder="Mín. 6 caracteres"
                  required
                  aria-required="true"
                />
              </div>
            )}
            {selectedUsuario && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Nova senha</label>
                <Input
                  type="password"
                  value={usuarioForm.password}
                  onChange={(e) => setUsuarioForm({ ...usuarioForm, password: e.target.value })}
                  placeholder="Deixe em branco para manter a atual"
                  aria-describedby="senha-edit-hint"
                />
                <p id="senha-edit-hint" className="text-xs text-muted-foreground mt-1">
                  Preencha apenas se quiser alterar a senha
                </p>
              </div>
            )}
          </div>

          {/* Vínculo com cliente (push e identificação) — combobox de busca */}
          <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Vínculo com cliente
            </h3>
            <div className="space-y-1">
              <label className="block text-sm font-medium mb-1.5">Buscar e selecionar cliente</label>
              {loadingClientesVinculo ? (
                <p className="text-sm text-muted-foreground py-3 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Carregando lista de clientes...
                </p>
              ) : (
                <div
                  className="relative"
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setClienteComboboxOpen(false);
                    }
                  }}
                >
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                    <Input
                      type="text"
                      value={
                        usuarioForm.clienteFornecedorId && !clienteComboboxOpen
                          ? (() => {
                              const c = clientesFornecedores.find((cf) => cf.id === usuarioForm.clienteFornecedorId);
                              return c ? `${c.nome}${(c as any).codigo ? ` (${(c as any).codigo})` : ''}` : '';
                            })()
                          : buscaClienteVinculo
                      }
                      onChange={(e) => {
                        setBuscaClienteVinculo(e.target.value);
                        setClienteComboboxOpen(true);
                      }}
                      onFocus={() => setClienteComboboxOpen(true)}
                      placeholder="Digite nome, código ou telefone..."
                      className="pl-9 pr-9 rounded-md border border-input bg-background"
                      aria-label="Buscar e selecionar cliente"
                      aria-expanded={clienteComboboxOpen}
                      aria-haspopup="listbox"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                      {usuarioForm.clienteFornecedorId && (
                        <button
                          type="button"
                          onClick={() => {
                            setUsuarioForm({ ...usuarioForm, clienteFornecedorId: '' });
                            setBuscaClienteVinculo('');
                            setClienteComboboxOpen(true);
                          }}
                          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-muted-foreground"
                          aria-label="Desmarcar cliente"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                      {!usuarioForm.clienteFornecedorId && buscaClienteVinculo && (
                        <button
                          type="button"
                          onClick={() => setBuscaClienteVinculo('')}
                          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-muted-foreground"
                          aria-label="Limpar busca"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {clienteComboboxOpen && (
                    <ul
                      role="listbox"
                      className="absolute z-20 w-full mt-1 max-h-56 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-card shadow-lg py-1 text-sm"
                    >
                      <li
                        role="option"
                        className="px-3 py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 text-muted-foreground border-b border-gray-100 dark:border-slate-700 dark:border-slate-700 dark:border-gray-800"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setUsuarioForm((prev) => ({ ...prev, clienteFornecedorId: '' }));
                          setBuscaClienteVinculo('');
                          setClienteComboboxOpen(false);
                        }}
                      >
                        Nenhum — criar novo cliente com os dados abaixo
                      </li>
                      {(() => {
                        const term = buscaClienteVinculo.trim().toLowerCase();
                        const filtrados = term
                          ? clientesFornecedores.filter((cf) => {
                              const nome = String(cf.nome ?? '').toLowerCase();
                              const codigo = String((cf as any).codigo ?? '').toLowerCase();
                              const tel = String((cf as any).telefone ?? (cf as any).whatsapp ?? '').toLowerCase();
                              return nome.includes(term) || codigo.includes(term) || tel.includes(term);
                            })
                          : clientesFornecedores;
                        if (filtrados.length === 0) {
                          return (
                            <li className="px-3 py-4 text-center text-muted-foreground">
                              Nenhum cliente encontrado. Use a opção acima para criar novo.
                            </li>
                          );
                        }
                        return filtrados.map((cf) => (
                          <li
                            key={cf.id}
                            role="option"
                            aria-selected={usuarioForm.clienteFornecedorId === cf.id}
                            className={`px-3 py-2.5 cursor-pointer hover:bg-primary/10 dark:hover:bg-primary/20 flex flex-col gap-0.5 ${
                              usuarioForm.clienteFornecedorId === cf.id
                                ? 'bg-primary/5 dark:bg-primary/10 border-l-2 border-primary'
                                : ''
                            }`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              const newId = cf.id;
                              const clienteCpfCnpj = (cf as any).cpfcnpj || (cf as any).cpfCnpj || '';
                              setUsuarioForm((prev) => ({
                                ...prev,
                                clienteFornecedorId: newId,
                                nome: cf.nome || prev.nome,
                                telefone: (cf as any).telefone || (cf as any).whatsapp || prev.telefone,
                                cpfCnpj: clienteCpfCnpj || prev.cpfCnpj,
                              }));
                              setBuscaClienteVinculo('');
                              setClienteComboboxOpen(false);
                            }}
                          >
                            <span className="font-medium text-gray-900 dark:text-gray-100">{cf.nome}</span>
                            {(cf as any).codigo && (
                              <span className="text-xs text-muted-foreground">{(cf as any).codigo}</span>
                            )}
                          </li>
                        ));
                      })()}
                    </ul>
                  )}
                </div>
              )}
              {!loadingClientesVinculo && clientesFornecedores.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Nenhum cliente cadastrado. Será criado um novo com os dados informados abaixo.
                </p>
              )}
              <p id="cliente-hint" className="text-xs text-muted-foreground mt-1.5">
                Usuários vinculados a um cliente podem receber notificações push no app. Se não selecionar, um novo cliente será criado com nome, CPF/CNPJ e telefone informados abaixo.
              </p>
            </div>
          </div>

          {/* Dados do cliente (nome, CPF, telefone) */}
          <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {usuarioForm.clienteFornecedorId ? 'Dados de contato do cliente' : 'Dados do novo cliente'}
            </h3>
            {!usuarioForm.clienteFornecedorId && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Nome completo {!selectedUsuario && <span className="text-red-500">*</span>}</label>
                <Input
                  value={usuarioForm.nome}
                  onChange={(e) => setUsuarioForm({ ...usuarioForm, nome: e.target.value })}
                  placeholder="Nome completo"
                  required={!selectedUsuario}
                />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">CPF/CNPJ</label>
                <Input
                  value={usuarioForm.cpfCnpj}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setUsuarioForm({ ...usuarioForm, cpfCnpj: value });
                  }}
                  placeholder="Somente números"
                  maxLength={14}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Telefone</label>
                <Input
                  value={usuarioForm.telefone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setUsuarioForm({ ...usuarioForm, telefone: value });
                  }}
                  placeholder="DDD + número"
                  maxLength={11}
                />
              </div>
            </div>
          </div>

          {!selectedUsuario && (
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="usuario-ativo"
                checked={usuarioForm.isActive}
                onChange={(e) => setUsuarioForm({ ...usuarioForm, isActive: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="usuario-ativo" className="text-sm font-medium">
                Usuário ativo
              </label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" onClick={() => setShowUsuarioModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveUsuario} loading={savingUsuario}>
              <Save className="h-4 w-4 mr-2" />
              Salvar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Template */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title={selectedTemplate ? 'Editar Template' : 'Novo Template'}
        size="xl"
      >
        <div className="space-y-6">
          {/* Informações gerais */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Informações gerais
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Nome</label>
                <Input
                  value={templateForm.nome}
                  onChange={(e) => setTemplateForm({ ...templateForm, nome: e.target.value })}
                  placeholder="Ex: Template Padrão - Primeiro Acesso"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Tipo</label>
                <Select
                  value={templateForm.tipo}
                  onValueChange={(value) => setTemplateForm({ ...templateForm, tipo: value as 'first_access' | 'reset_password' })}
                >
                  <option value="first_access">Primeiro Acesso</option>
                  <option value="reset_password">Recuperação de Senha</option>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Assunto do e-mail</label>
              <Input
                value={templateForm.assunto}
                onChange={(e) => setTemplateForm({ ...templateForm, assunto: e.target.value })}
                placeholder="Ex: Código de Primeiro Acesso - ZehMotoca"
              />
            </div>
          </div>

          {/* Conteúdo do e-mail */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground border-b pb-2 flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Conteúdo do e-mail
            </h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Corpo HTML</label>
              <textarea
                className="w-full min-h-[220px] p-3 border border-input rounded-md font-mono text-sm bg-muted/20 focus:bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                value={templateForm.corpoHtml}
                onChange={(e) => setTemplateForm({ ...templateForm, corpoHtml: e.target.value })}
                placeholder="<h1>Olá, {{nome}}!</h1><p>Seu código é: <strong>{{codigo}}</strong></p>"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                Variáveis: <code className="bg-muted px-1 rounded">{'{{nome}}'}</code>, <code className="bg-muted px-1 rounded">{'{{codigo}}'}</code>, <code className="bg-muted px-1 rounded">{'{{tempo_expiracao}}'}</code>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Corpo texto (opcional)</label>
              <textarea
                className="w-full min-h-[100px] p-3 border border-input rounded-md font-mono text-sm bg-muted/20 focus:bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                value={templateForm.corpoTexto}
                onChange={(e) => setTemplateForm({ ...templateForm, corpoTexto: e.target.value })}
                placeholder="Olá, {{nome}}! Seu código é: {{codigo}}"
                spellCheck={false}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              Cancelar
            </Button>
            <Button variant="outline" onClick={handlePreviewFromForm} className="gap-2">
              <Eye className="h-4 w-4" />
              Visualizar preview
            </Button>
            <Button onClick={handleSaveTemplate} loading={savingTemplate} className="gap-2">
              <Save className="h-4 w-4" />
              Salvar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Preview */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Preview do Template"
        size="xl"
      >
        <div className="space-y-4">
          {previewAssunto && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Assunto</p>
              <p className="text-sm font-medium text-foreground">{previewAssunto}</p>
            </div>
          )}
          <div className="rounded-lg border bg-card overflow-hidden">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 pt-3 pb-1">Corpo do e-mail</p>
            <div className="min-h-[200px] p-4 max-h-[60vh] overflow-auto">
              {previewContent ? (
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewContent }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">Nenhum conteúdo para exibir. Edite o template e clique em &quot;Visualizar preview&quot;.</p>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal de Observações */}
      <Modal
        isOpen={showObservacoesModal}
        onClose={() => setShowObservacoesModal(false)}
        title="Observações da Indicação"
        size="lg"
      >
        <div className="space-y-4">
          {selectedIndicacao && (
            <>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-2">
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Indicado por:</span>
                  <p className="font-semibold">{selectedIndicacao.clienteFornecedorUser.clienteFornecedor.nome}</p>
                  <p className="text-sm text-muted-foreground">{selectedIndicacao.clienteFornecedorUser.email}</p>
                </div>
                <div className="border-t pt-2 mt-2">
                  <span className="text-sm font-medium text-muted-foreground">Amigo indicado:</span>
                  <p className="font-semibold">{selectedIndicacao.nomeIndicado}</p>
                  <p className="text-sm text-muted-foreground">{selectedIndicacao.telefoneIndicado}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Observações</label>
                <Textarea
                  value={observacoesText}
                  onChange={(e) => setObservacoesText(e.target.value)}
                  placeholder="Adicione observações sobre esta indicação..."
                  rows={6}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowObservacoesModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveObservacoes} loading={savingObservacoes}>
                  Salvar
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

// Componente de Card de Indicação
interface IndicacaoCardProps {
  indicacao: Indicacao;
  onUpdateStatus: (indicacao: Indicacao, status: 'pendente' | 'em_contato' | 'convertido' | 'descartado') => void;
  onOpenObservacoes: (indicacao: Indicacao) => void;
}

const IndicacaoCard: React.FC<IndicacaoCardProps> = ({ indicacao, onUpdateStatus, onOpenObservacoes }) => {
  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className="space-y-3">
      {/* Header do Card */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="w-4 h-4 text-primary" />
            <h4 className="font-semibold text-sm">{indicacao.nomeIndicado}</h4>
          </div>
          <div className="space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              <span>{formatPhone(indicacao.telefoneIndicado)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Informações do Usuário que Indicou */}
      <div className="pt-2 border-t space-y-1">
        <div className="text-xs font-medium text-muted-foreground">Indicado por:</div>
        <div className="text-xs">
          <div className="font-semibold">{indicacao.clienteFornecedorUser.clienteFornecedor.nome}</div>
          <div className="text-muted-foreground">{indicacao.clienteFornecedorUser.email}</div>
          {indicacao.clienteFornecedorUser.clienteFornecedor.cpfCnpj && (
            <div className="text-muted-foreground">CPF/CNPJ: {indicacao.clienteFornecedorUser.clienteFornecedor.cpfCnpj}</div>
          )}
        </div>
      </div>

      {/* Observações */}
      {indicacao.observacoes && (
        <div className="pt-2 border-t">
          <div className="text-xs font-medium text-muted-foreground mb-1">Observações:</div>
          <p className="text-xs text-muted-foreground line-clamp-2">{indicacao.observacoes}</p>
        </div>
      )}

      {/* Data */}
      <div className="text-xs text-muted-foreground">
        {new Date(indicacao.createdAt).toLocaleDateString('pt-BR')}
      </div>

      {/* Ações */}
      <div className="flex items-center gap-2 pt-2 border-t">
        <Button
          variant="outline"
          onClick={() => onOpenObservacoes(indicacao)}
          className="flex-1 text-xs py-1.5"
        >
          <FileText className="w-3 h-3 mr-1" />
          Observações
        </Button>
        <Select
          value={indicacao.status}
          onValueChange={(value) => onUpdateStatus(indicacao, value as any)}
          className="flex-1 text-xs"
        >
          <option value="pendente">Pendente</option>
          <option value="em_contato">Em Contato</option>
          <option value="convertido">Convertido</option>
          <option value="descartado">Descartado</option>
        </Select>
      </div>
    </div>
  );
};
